import { useState } from 'react';
import {
  type CreativeMedia,
  useCreativeRuns,
  useCreativeThumbnails,
  usePlatformsById,
  useRescoreCreative,
  useRunResult,
} from '../../lib/workflows.hooks.js';
import { type CreativeRun, runsFor } from './creativeRuns.js';
import {
  type CreativeHalves,
  type CreativePlatformScore,
  campaignMetadataOf,
  creativeHalves,
  creativeScores,
  creativesAcross,
  inOrder,
  mergeRescored,
  type ScoredCreative,
} from './scoredBatch.js';

export interface CreativeDrawerProps {
  open: boolean;
  runId: string | null;
  result: unknown;
  creativeId: string | null;
  networkId: number | null;
  onOpenCreative: (creativeId: string, networkId: number) => void;
  order?: string[];
}

export interface CreativeDrawerView {
  batch: unknown;
  creative: ScoredCreative | undefined;
  creativeCount: number;
  creativeAt: number;
  file: CreativeMedia | null;
  scores: CreativePlatformScore[];
  network: CreativePlatformScore | undefined;
  halves: CreativeHalves | null;
  entered: Record<string, string>;
  runs: CreativeRun[];
  runAt: number;
  busy: boolean;
  failed: string | null;
  openNetwork: (networkId: number) => void;
  openRun: (at: number) => void;
  stepCreative: (by: number) => void;
  rescore: (selected: Record<string, string>) => void;
}

export function useCreativeDrawer({
  open,
  runId,
  result,
  creativeId,
  networkId,
  onOpenCreative,
  order,
}: CreativeDrawerProps): CreativeDrawerView {
  const platforms = usePlatformsById();
  const media = useCreativeThumbnails(open ? runId : null);
  const rescoreCreative = useRescoreCreative();
  const [chosenNetworkId, setChosenNetworkId] = useState<number | null>(null);
  const [chosenRunAt, setChosenRunAt] = useState<number | null>(null);

  const platformOf = (networkId: number): { slug: string; label: string } =>
    platforms[networkId] ?? { slug: '', label: String(networkId) };
  const networkOf = (batch: unknown, creative: ScoredCreative | undefined) => {
    const scores =
      creative === undefined ? [] : creativeScores(batch, creative.creativeId, platformOf);
    return {
      scores,
      network: scores.find((row) => row.networkId === (chosenNetworkId ?? networkId)) ?? scores[0],
    };
  };

  const startedRunId = rescoreCreative.data?.runId ?? null;
  const { runs: history } = useCreativeRuns(open ? runId : null, startedRunId);
  const landed = startedRunId !== null && history.some((entry) => entry.runId === startedRunId);

  const scored = inOrder(creativesAcross(result), order);
  const creativeAt = scored.findIndex((entry) => entry.creativeId === creativeId);
  const openedNetwork = networkOf(result, scored[creativeAt]).network;
  const runs =
    scored[creativeAt] === undefined || openedNetwork === undefined
      ? []
      : runsFor(history, scored[creativeAt].creativeId, openedNetwork.networkId);
  const runAt = chosenRunAt === null ? runs.length - 1 : Math.min(chosenRunAt, runs.length - 1);

  const viewedRun = runs[runAt];
  const selectedRunId =
    viewedRun === undefined || viewedRun.runId === runId ? null : viewedRun.runId;
  const viewed = useRunResult(landed || startedRunId === null ? selectedRunId : startedRunId);
  const rescoreFailed = !landed && viewed.data?.status === 'failed';

  const batch =
    viewed.data?.status === 'completed' ? mergeRescored(result, viewed.data.result) : result;
  const creative = inOrder(creativesAcross(batch), order)[creativeAt];
  const { scores, network } = networkOf(batch, creative);

  return {
    batch,
    creative,
    creativeCount: scored.length,
    creativeAt,
    file: media[creative?.creativeId ?? ''] ?? null,
    scores,
    network,
    halves:
      creative === undefined || network === undefined
        ? null
        : creativeHalves(batch, creative.creativeId, network.networkId),
    entered:
      creative === undefined || network === undefined
        ? {}
        : campaignMetadataOf(batch, creative.creativeId, network.networkId),
    runs,
    runAt,
    busy: rescoreCreative.isPending || (startedRunId !== null && !landed && !rescoreFailed),
    failed:
      rescoreCreative.error?.message ??
      (viewed.data?.status === 'failed' ? viewed.data.errorMessage : null),
    openNetwork: (networkId) => {
      setChosenRunAt(null);
      setChosenNetworkId(networkId);
    },
    openRun: setChosenRunAt,
    stepCreative: (by) => {
      const next = scored[creativeAt + by];
      if (next === undefined || network === undefined) return;
      setChosenRunAt(null);
      onOpenCreative(next.creativeId, network.networkId);
    },
    rescore: (selected) => {
      if (creative === undefined || network === undefined || runId === null) return;
      setChosenRunAt(null);
      rescoreCreative.mutate({
        runId,
        creativeId: creative.creativeId,
        networkId: network.networkId,
        selected,
      });
    },
  };
}

export function useLatestBatch(runId: string | null, result: unknown) {
  const { runs, folds } = useCreativeRuns(runId);
  return {
    runs,
    latest: folds.reduce((batch: unknown, fold) => mergeRescored(batch, fold), result),
  };
}
